/**
 * The delay, with a number on it.
 *
 * ---------------------------------------------------------------------
 * THE SENTENCE IS THE SCREEN
 *
 * This exists to finish a sentence a superintendent has been saying at
 * half five in the morning for thirty years: "minus one at dawn, we're
 * on a delay till eight, carts on the path." Everything above the fold
 * is that sentence with the missing half added — how many people, how
 * many hours, what it costs — and everything below it is the working.
 *
 * So the headline is one line of prose, not a grid of tiles. Somebody
 * reading this is holding a telephone.
 *
 * ---------------------------------------------------------------------
 * THE FIGURE IS FETCHED SEPARATELY AND SOMETIMES REFUSED
 *
 * The list of names comes with the advisory. The money is a second
 * request, because the database will not put a per-person cost on the
 * wire and this screen must not assemble one either.
 *
 * When fewer than three people are affected the answer is a sentence
 * saying it is withheld, and that sentence is shown as written. It is
 * not an error and it is not a dash: "we are not showing you this,
 * because with two names beside it a total is one person's pay" is
 * something the reader deserves to be told once rather than left to
 * wonder about every winter.
 *
 * ---------------------------------------------------------------------
 * THE SALARIED ONES ARE EXPLAINED, NOT HIDDEN
 *
 * Five people idle for fourteen hours saving a hundred and forty-nine
 * dollars looks like a bug until you know two of them are the
 * superintendents, who are paid the same whether the course opened at
 * six or at nine. The screen says so next to the figure. A number that
 * needs explaining and does not explain itself is a number people stop
 * trusting, and then stop using.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { date as fmtDate, money, titleCase } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status } from '../components/ui.tsx';

interface Advisory {
  id: string;
  course_id: string;
  course: string;
  play_date: string;
  proposed_status: string;
  proposed_first_tee_at: string | null;
  proposed_reason: string | null;
  decided_status: string | null;
  decided_first_tee_at: string | null;
  proposal_outcome: string;
  shifts_affected: number;
  staff_affected: number;
  staff_hourly: number;
  staff_salaried: number;
  staff_unpriced: number;
  idle_minutes: number;
  idle_hours: string;
  saving_available: boolean;
  computed_at: string;
}

interface Person {
  id: string;
  display_name: string | null;
  job_title: string | null;
  department: string;
  starts_at: string;
  ends_at: string;
  idle_hours: string;
  idles: string;
  rule_note: string | null;
  unfilled: boolean;
}

interface Outcome {
  idle_minutes_advised: number;
  worked_in_window: number;
  minutes_not_worked: number;
  advice_followed: string | null;
}

const hhmm = (t: string | null) => (t ? t.slice(0, 5) : '—');

/** The sentence, assembled. */
function headline(a: Advisory): string {
  const held =
    a.proposed_status === 'closed'
      ? 'The course is closed'
      : `The course is held until ${hhmm(a.proposed_first_tee_at)}`;
  const why = a.proposed_reason ? ` for ${a.proposed_reason}` : '';
  const who =
    a.staff_affected === 0
      ? 'and nobody is rostered into it.'
      : `and ${a.staff_affected} ${a.staff_affected === 1 ? 'person has' : 'people have'} ` +
        `nothing to do until then — ${a.idle_hours} hours between them.`;
  return `${held}${why}, ${who}`;
}

export function WeatherStaffingPage() {
  const [open, setOpen] = useState<string | null>(null);
  const { data, error, loading, reload } = useApi<{ advisories: Advisory[] }>(
    '/v1/staffing/advisories?days=120',
  );

  if (loading && !data) return <Loading />;

  const advisories = data?.advisories ?? [];

  return (
    <>
      <h1>Weather &amp; Staffing</h1>
      <ErrorBanner error={error} />

      <p className="muted small">
        When the course is held, the crew rostered to be on it are already here. This is who
        they are and what the idle time costs. Nothing here cancels a shift or tells anybody to
        go home — the professional walks the course and the software does not.
      </p>

      {advisories.length === 0 ? (
        <Empty>
          No held days have been priced. When the course is delayed or closed, price the day
          from the course record and it will appear here.
        </Empty>
      ) : (
        advisories.map((a) => (
          <Card
            key={a.id}
            title={`${fmtDate(a.play_date)} · ${a.course}`}
          >
            <p>{headline(a)}</p>

            <div className="actions">
              <Status tone={a.proposed_status === 'closed' ? 'serious' : 'warning'}>
                {titleCase(a.proposed_status)}
              </Status>
              <Status
                tone={
                  a.proposal_outcome === 'agreed'
                    ? 'good'
                    : a.proposal_outcome === 'overruled'
                      ? 'warning'
                      : 'neutral'
                }
              >
                {a.proposal_outcome === 'unanswered'
                  ? 'Nobody has answered this yet'
                  : a.proposal_outcome === 'agreed'
                    ? 'The shop agreed'
                    : 'The shop decided otherwise'}
              </Status>
              <button className="linkish" onClick={() => setOpen(open === a.id ? null : a.id)}>
                {open === a.id ? 'Close' : 'Who, and what it costs'}
              </button>
            </div>

            {open === a.id && <Detail advisory={a} onChanged={reload} />}
          </Card>
        ))
      )}
    </>
  );
}

// =====================================================================
// One morning
// =====================================================================

function Detail({ advisory, onChanged }: { advisory: Advisory; onChanged: () => void }) {
  const { currency } = useSession();

  const { data, error, loading } = useApi<{
    advisory: Advisory;
    people: Person[];
    outcome: Outcome | null;
  }>(`/v1/staffing/advisories/${advisory.id}`, [advisory.id]);

  const saving = useApi<{
    available: boolean;
    withheld_why: string | null;
    staff_affected: number;
    saving_minor: number | null;
  }>(`/v1/staffing/advisories/${advisory.id}/saving`, [advisory.id]);

  const recompute = useMutation(async () => {
    const r = await api('/v1/staffing/advisories', {
      method: 'POST',
      body: {
        course_id: advisory.course_id,
        play_date: advisory.play_date,
        status: advisory.proposed_status,
        first_tee_at: advisory.proposed_first_tee_at,
        reason: advisory.proposed_reason,
      },
    });
    onChanged();
    return r;
  });

  if (loading && !data) return <Loading rows={2} />;
  if (!data) return null;

  const people = data.people ?? [];
  const outcome = data.outcome;

  return (
    <div>
      <ErrorBanner error={error ?? saving.error ?? recompute.error} />

      {/* The figure, or the reason there isn't one. */}
      <div className="notice">
        {saving.loading ? (
          <span className="muted">Working out what it costs…</span>
        ) : saving.data?.available ? (
          <>
            <strong>
              Starting them late saves {money(saving.data.saving_minor ?? 0, currency)}.
            </strong>
            {advisory.staff_salaried > 0 && (
              <p className="muted small">
                {advisory.staff_hourly} of the {advisory.staff_affected} are paid by the hour.
                The other {advisory.staff_salaried}{' '}
                {advisory.staff_salaried === 1 ? 'is' : 'are'} salaried and cost the club the
                same whether the course opened at six or at nine, so their idle hours are real
                and their idle cost is nothing.
              </p>
            )}
            {advisory.staff_unpriced > 0 && (
              <p className="muted small">
                {advisory.staff_unpriced} of them{' '}
                {advisory.staff_unpriced === 1 ? 'has' : 'have'} no pay rate on file, so{' '}
                {advisory.staff_unpriced === 1 ? 'their' : 'their'} idle time is not in this
                figure. The real saving is higher than this.
              </p>
            )}
          </>
        ) : (
          <span className="muted">{saving.data?.withheld_why ?? 'No figure available.'}</span>
        )}
      </div>

      {people.length === 0 ? (
        <Empty>Nobody is rostered into the held part of the day.</Empty>
      ) : (
        <div className="table">
          <table className="data data--cards">
            <thead>
              <tr>
                <th>Who</th>
                <th>Department</th>
                <th>Rostered</th>
                <th className="num">Idle</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <tr key={p.id} className={p.unfilled ? 'muted' : undefined}>
                  <td data-label="Who" className="cell--title">
                    {p.unfilled ? (
                      <span className="muted">Unfilled shift — nobody to call</span>
                    ) : (
                      <>
                        {p.display_name}
                        <span className="muted small"> · {p.job_title}</span>
                      </>
                    )}
                  </td>
                  <td data-label="Department" className="muted">
                    {p.department}
                  </td>
                  <td data-label="Rostered">
                    {hhmm(p.starts_at)}–{hhmm(p.ends_at)}
                  </td>
                  <td data-label="Idle" className="num">
                    {p.idle_hours}h
                  </td>
                  <td data-label="Why" className="muted small cell--aside">
                    {p.rule_note ??
                      (p.idles === 'whole_day' ? 'The whole day' : 'Until play begins')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Was the advice taken. 0076 stored both halves so this could be
          asked; this is the asking. */}
      {outcome && outcome.idle_minutes_advised > 0 && (
        <p className="muted small">
          {Number(outcome.advice_followed) >= 0.9
            ? 'The clock says almost none of those hours were worked — the club acted on this.'
            : Number(outcome.advice_followed) <= 0.1
              ? 'The clock says those hours were worked anyway. The club did not act on this, ' +
                'which is a perfectly good answer and worth knowing before trusting the figure ' +
                'next winter.'
              : `The clock says ${Math.round(Number(outcome.advice_followed) * 100)}% of those ` +
                'hours were not worked.'}
        </p>
      )}

      <div className="actions">
        <button className="secondary small" disabled={recompute.busy} onClick={() => recompute.run()}>
          {recompute.busy ? 'Recomputing…' : 'Recompute against the roster as it stands'}
        </button>
      </div>
    </div>
  );
}
